import { selectedFeatureAtom } from '@/atoms/selection.atom';
import { pipeToolModeAtom } from '@/atoms/pipe.atom';
import type { FeaturesQuery } from '@/graphql/graphql';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { parsePipeProperties } from '@/lib/pipe/feature';
import type { Vec3 } from '@/lib/pipe/types';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import * as THREE from 'three';

/**
 * Renders every saved pipe feature as a translucent tube along its centerline
 * (at the detected radius). Clicking the tube SELECTS the feature
 * (`selectedFeatureAtom`) — the same path the rest of the viewer uses — so the
 * standard FeatureProperties panel (with delete) opens. No billboard label.
 *
 * The pipe currently being edited is skipped (PipeTool draws its live,
 * draggable preview). A centerline with >2 vertices (chained segments) renders
 * as one cylinder per consecutive pair.
 */

type Feature = FeaturesQuery['featuresByLayer'][number];

const PIPE_COLOR = '#22d3ee';

interface SavedPipe {
  feature: Feature;
  radius: number;
  /** Centerline vertices (≥2). */
  vertices: Vec3[];
}

const SavedPipeTube = ({
  pipe,
  onSelect,
}: {
  pipe: SavedPipe;
  onSelect: (feature: Feature) => void;
}) => {
  const [hovered, setHovered] = useState(false);

  // One cylinder per consecutive vertex pair. Each is placed in a group at its
  // own midpoint (small local coords → no UTM jitter) and oriented via an Euler
  // tuple (avoids three's read-only transform-prop pitfall with class instances).
  const segments = useMemo(() => {
    const out: { mid: Vec3; rotation: [number, number, number]; length: number }[] = [];
    for (let i = 0; i + 1 < pipe.vertices.length; i++) {
      const a = pipe.vertices[i];
      const b = pipe.vertices[i + 1];
      const dir = new THREE.Vector3(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
      const len = dir.length();
      if (len <= 1e-9) continue;
      const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
      const e = new THREE.Euler().setFromQuaternion(q, 'XYZ');
      out.push({
        mid: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2],
        rotation: [e.x, e.y, e.z],
        length: len,
      });
    }
    return out;
  }, [pipe.vertices]);

  if (pipe.radius <= 0 || segments.length === 0) return null;

  const setCursor = (c: string) => {
    document.body.style.cursor = c;
  };

  return (
    <>
      {segments.map((s, i) => (
        <group key={i} position={[s.mid[0], s.mid[1], s.mid[2]]}>
          <mesh
            rotation={s.rotation}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(pipe.feature);
            }}
            onPointerOver={(e) => {
              e.stopPropagation();
              setHovered(true);
              setCursor('pointer');
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              setHovered(false);
              setCursor('default');
            }}>
            <cylinderGeometry args={[pipe.radius, pipe.radius, s.length, 24, 1, false]} />
            <meshStandardMaterial
              color={PIPE_COLOR}
              transparent
              opacity={hovered ? 0.6 : 0.42}
              side={THREE.DoubleSide}
              roughness={0.6}
              metalness={0.1}
            />
          </mesh>
        </group>
      ))}
    </>
  );
};

export const SavedPipes = ({ features }: { features: Feature[] }) => {
  const mode = useAtomValue(pipeToolModeAtom);
  const setSelectedFeature = useSetAtom(selectedFeatureAtom);
  const { createUpdateHandler } = useFeatureActions();

  const pipes = useMemo(() => {
    const out: SavedPipe[] = [];
    for (const f of features) {
      const props = parsePipeProperties(f.properties);
      if (!props) continue; // null ⇒ not a pipe; doubles as the kind filter
      const geom = f.geometry as { type?: string; coordinates?: number[][] } | null;
      if (!geom || geom.type !== 'LineString' || !geom.coordinates || geom.coordinates.length < 2) continue;
      const vertices: Vec3[] = geom.coordinates.map((c) => [c[0], c[1], c[2] ?? 0]);
      out.push({ feature: f, radius: props.radiusM, vertices });
    }
    return out;
  }, [features]);

  const handleSelect = (feature: Feature) => {
    if (!feature.layerId) return;
    // Same selection path as GeoJson / SavedStockpiles → opens FeatureProperties
    // (which provides delete + property editing).
    setSelectedFeature({
      ...feature,
      onSave: createUpdateHandler(feature.id, feature.layerId),
    });
  };

  if (pipes.length === 0) return null;

  return (
    <>
      {pipes.map((p) =>
        // Skip the pipe being edited — PipeTool draws its live, draggable preview.
        mode.editFeatureId === p.feature.id ? null : (
          <SavedPipeTube key={p.feature.id} pipe={p} onSelect={handleSelect} />
        ),
      )}
    </>
  );
};
